import { useSmsAutomationAccess } from './useSmsAutomationAccess';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@libs/axios/axios';
import { apiCall } from '@shared/entity';
import { useAuthContext } from '@shared/auth/AuthContext';

export const SMS_PROFILE_URL = '/hairdresser/profile#profile-sms-credit-section';
export const smsMoney = (halers: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK' }).format(halers / 100);
export interface SmsSummary {
  balanceHalers: number;
  reservedHalers: number;
  requiredHalers: number;
  shortfallHalers: number;
  forecastDays: number;
  plannedMessages: number;
  plannedParts: number;
  lowCredit: boolean;
  lastTopUpAmount: number | null;
  presets: number[];
  priceHalers: number;
  automatic: boolean;
  available: boolean;
}
export const smsStatuses = {
  SENT: 'sms.status.SENT',
  DELIVERED: 'sms.status.DELIVERED',
  PLANNED: 'sms.status.PLANNED',
  SKIPPED: 'sms.status.SKIPPED',
  FAILED: 'sms.status.FAILED',
  UNDELIVERED: 'sms.status.UNDELIVERED',
  SENDING: 'sms.status.SENDING',
  UNKNOWN: 'sms.status.UNKNOWN',
};
export interface SmsMessage {
  id: string;
  clientId: string;
  clientName: string;
  phone: string;
  content: string;
  appointmentAt: string;
  scheduledAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  status: keyof typeof smsStatuses;
  parts: number;
  costHalers: number;
  chargedHalers: number;
  reservedHalers: number;
  reason: string | null;
}
export interface SmsHistory {
  messages: SmsMessage[];
  count: number;
  page: number;
  chargedHalers: number;
  reservedHalers: number;
}
export interface SmsTopUp {
  id: string;
  refId: string;
  amountHalers: number;
  createdAt: string;
  creditedAt: string | null;
  status: string;
  redirect: string | null;
}
export function useSmsSummary() {
  const { t } = useIntlTranslations();
  const { isDemo } = useAuthContext();
  const smsAutomationEnabled = useSmsAutomationAccess();
  return useQuery<SmsSummary>({
    queryKey: ['sms', 'summary'],
    enabled: !isDemo && smsAutomationEnabled,
    queryFn: () => apiCall(() => axiosInstance.get('/api/sms/summary'), t('smsCredit.loadError')),
    staleTime: 30000,
    refetchInterval: 60000,
  });
}
export function useSmsHistory(params: {
  clientId?: string;
  page: number;
  status: string;
  from: string;
  to: string;
  search: string;
}) {
  const { t } = useIntlTranslations();
  const { isDemo } = useAuthContext();
  const smsAutomationEnabled = useSmsAutomationAccess();
  return useQuery<SmsHistory>({
    queryKey: ['sms', 'history', params],
    enabled: !isDemo && smsAutomationEnabled,
    queryFn: () =>
      apiCall(
        () =>
          axiosInstance.get('/api/sms/history', {
            params: {
              ...params,
              status: params.status || undefined,
              from: params.from || undefined,
              to: params.to || undefined,
            },
          }),
        t('smsHistory.loadError'),
      ),
    refetchInterval: 60000,
  });
}
export function useSmsTopUps() {
  const { t } = useIntlTranslations();
  const { isDemo } = useAuthContext();
  const smsAutomationEnabled = useSmsAutomationAccess();
  return useQuery<SmsTopUp[]>({
    queryKey: ['sms', 'topUps'],
    enabled: !isDemo && smsAutomationEnabled,
    queryFn: () =>
      apiCall(() => axiosInstance.get('/api/sms/top-ups'), t('smsCredit.topUpsLoadError')),
    refetchInterval: 30000,
  });
}
export function useCreateSmsTopUp() {
  const { t } = useIntlTranslations();
  return useMutation<{ redirect: string }, Error, { amount: number; requestKey: string }>({
    mutationFn: body =>
      apiCall(() => axiosInstance.post('/api/sms/top-ups', body), t('smsCredit.createError')),
    onSuccess: data => {
      window.location.assign(data.redirect);
    },
  });
}
export function useRefreshSmsTopUp() {
  const { t } = useIntlTranslations();
  const cache = useQueryClient();
  return useMutation<{ status: string }, Error, string>({
    mutationFn: refId =>
      apiCall(
        () => axiosInstance.post(`/api/sms/top-ups/${encodeURIComponent(refId)}/refresh`),
        t('smsCredit.verifyError'),
      ),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: ['sms'] });
      cache.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}
